import { Space } from 'antd';
import { COMMODITY_TRANSLATED } from '@/common/messages';
import { Meters as messages } from '@/components/filter/messages';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { Typography } from '@/components/typography';
import { TypographySize } from '@/components/typography/enums/size';
import { TypographyType } from '@/components/typography/enums/type';
import { sortCommodities } from '@/utils/sort/sortCommodities';
import type { ViewMeters } from '@enmon/commons/lib/enmon';
import { isCommodity } from '@enmon/commons/lib/shared';
import { groupBy } from 'ramda';
import type { GetMetersResult } from '@/services/api/meters/types/results';
import type { PopulatedMeter } from '@/services/api/meters';
import { MeterSelectGroupBy } from './enums';
import { renderOptionLabelWithIdTag } from './renderOptionLabelWithIdTag';

const { Paragraph } = Typography;

const renderMeterOptionLabel = (
  { _id, name, serial, toolsId }: PopulatedMeter<ViewMeters.Dropdown>,
  search?: string,
) => (
  <Paragraph data-cy={`meter-select-meter-${_id}`}>
    <Space size="small">
      {renderOptionLabelWithIdTag(toolsId, name, search)}
      {serial && <FormattedMessage {...messages.serialShort} values={{ serial }} />}
    </Space>
  </Paragraph>
);

const groupMetersByCommodity = (includedMeters: PopulatedMeter<ViewMeters.Dropdown>[], search?: string) => {
  const groupedMeters = groupBy(meter => meter.commodity ?? '', includedMeters);
  // Sort commodities by priority
  return Object.entries(groupedMeters)
    .sort(([commodityA], [commodityB]) => {
      if (!(isCommodity(commodityA) && isCommodity(commodityB))) return 0;
      return sortCommodities(commodityA, commodityB);
    })
    .map(([commodity, meters]) => ({
      label: (
        <Paragraph
          bold
          data-cy={`meter-select-group-${commodity}`}
          size={TypographySize.Large}
          type={TypographyType.Normal}
        >
          {isCommodity(commodity) ? <FormattedMessage {...COMMODITY_TRANSLATED[commodity]} /> : commodity}
        </Paragraph>
      ),
      options: meters.map(meter => ({
        value: meter._id,
        label: renderMeterOptionLabel(meter, search),
        serial: meter.serial,
      })),
    }));
};

const groupMetersByEstablishment = (includedMeters: PopulatedMeter<ViewMeters.Dropdown>[], search?: string) => {
  const groupedMeters = groupBy(meter => meter.establishment.name, includedMeters);

  return Object.entries(groupedMeters)
    .sort()
    .map(([establishmentName, meters]) => ({
      label: (
        <Paragraph
          bold
          data-cy={`meter-select-group-${establishmentName}`}
          size={TypographySize.Large}
          type={TypographyType.Normal}
        >
          {establishmentName}
        </Paragraph>
      ),
      options: meters.map(meter => ({
        value: meter._id,
        label: renderMeterOptionLabel(meter, search),
      })),
    }));
};

export const getMeterSelectOptions = (
  metersDropdownResponse: GetMetersResult<ViewMeters.Dropdown> | undefined,
  excludedIds: string[],
  groupByField: MeterSelectGroupBy,
  search?: string,
) => {
  if (!metersDropdownResponse) return [];

  const { result } = metersDropdownResponse;
  const includedMeters = result.filter(({ _id }) => !excludedIds?.includes(_id));

  switch (groupByField) {
    case MeterSelectGroupBy.Commodity: {
      return groupMetersByCommodity(includedMeters, search);
    }
    case MeterSelectGroupBy.Establishment: {
      return groupMetersByEstablishment(includedMeters, search);
    }
    default: {
      return includedMeters.map(({ _id, name, serial, toolsId }) => ({
        value: _id,
        label: (
          <Paragraph data-cy={`meter-select-meter-${_id}`}>
            {renderOptionLabelWithIdTag(toolsId, name, search)}
          </Paragraph>
        ),
        serial,
      }));
    }
  }
};
